import React, { useState, useMemo, useCallback } from "react";
import { useAvailability } from "../../hooks/useBooking";

/* --- Helpers ------------------------------------------------ */
const pad = (n) => String(n).padStart(2, "0");
const toDateStr = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const DAY_HEADERS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

function isBeforeToday(y, m, d) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(y, m, d) < today;
}
function isTodayFn(y, m, d) {
  const t = new Date();
  return t.getFullYear() === y && t.getMonth() === m && t.getDate() === d;
}

// Returns true if a slot is not bookable: starts in less than 1 hour from now (today only)
function isSlotExpiredForDate(date, slotTime) {
  const now = new Date();
  const todayMidnight = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );
  const slotDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  if (slotDay.getTime() !== todayMidnight.getTime()) return false;
  const start = slotTime.split(" - ")[0].trim();
  const [h, m] = start.split(":").map(Number);
  const slotStart = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    h,
    m,
    0,
  );
  return slotStart.getTime() - now.getTime() < 60 * 60 * 1000;
}

// Returns true if every slot for today is already past the 1-hour cutoff
function allSlotsExpiredForToday(serviceId, customSlots) {
  const today = new Date();
  const slots = customSlots || getServiceDefaultSlots(serviceId);
  return slots.every((slot) => isSlotExpiredForDate(today, slot.time));
}

/* --- Status helpers ----------------------------------------- */
// 3-state only: open (no bookings), almost (has bookings), full (100%)
function getStatus(booked, capacity) {
  if (booked <= 0) return "open";
  if (booked >= capacity) return "full";
  return "almost";
}
const DOT = {
  open: "bg-emerald-500",
  almost: "bg-orange-500",
  full: "bg-red-500",
};

/* --- Service slot config ------------------------------------ */
function getServiceDefaultSlots(serviceId) {
  switch (serviceId) {
    case "jet-ski":
      return [
        { time: "09:00 - 10:00", capacity: 2, booked: 0 },
        { time: "11:00 - 12:00", capacity: 2, booked: 0 },
        { time: "16:00 - 17:00", capacity: 2, booked: 0 },
        { time: "18:00 - 19:00", capacity: 2, booked: 0 },
      ];
    case "luxury-yacht":
      return [
        { time: "10:00 - 14:00", capacity: 10, booked: 0 },
        { time: "16:00 - 20:00", capacity: 10, booked: 0 },
      ];
    case "bayliner": // all possible slots across both packages
      // Pkg 1 (30 min): 09:00-09:30, 10:00-10:30, 11:00-11:30, 14:00-14:30, 15:00-15:30, 16:00-16:30, 17:00-17:30
      // Pkg 2 (60 min): 09:00-10:00, 11:00-12:00, 14:00-15:00, 16:00-17:00
      return [
        { time: "09:00 - 09:30", capacity: 5, booked: 0 },
        { time: "09:00 - 10:00", capacity: 5, booked: 0 },
        { time: "10:00 - 10:30", capacity: 5, booked: 0 },
        { time: "11:00 - 11:30", capacity: 5, booked: 0 },
        { time: "11:00 - 12:00", capacity: 5, booked: 0 },
        { time: "14:00 - 14:30", capacity: 5, booked: 0 },
        { time: "14:00 - 15:00", capacity: 5, booked: 0 },
        { time: "15:00 - 15:30", capacity: 5, booked: 0 },
        { time: "16:00 - 16:30", capacity: 5, booked: 0 },
        { time: "16:00 - 17:00", capacity: 5, booked: 0 },
        { time: "17:00 - 17:30", capacity: 5, booked: 0 },
      ];
    default: // speedboat — ALL possible slots across all 4 packages
      return [
        { time: "09:00 - 10:00", capacity: 11, booked: 0 },
        { time: "09:00 - 10:30", capacity: 11, booked: 0 },
        { time: "09:00 - 11:00", capacity: 11, booked: 0 },
        { time: "11:00 - 12:00", capacity: 11, booked: 0 },
        { time: "15:00 - 16:00", capacity: 11, booked: 0 },
        { time: "17:00 - 18:30", capacity: 11, booked: 0 },
        { time: "18:00 - 19:00", capacity: 11, booked: 0 },
        { time: "18:00 - 20:00", capacity: 11, booked: 0 },
      ];
  }
}

/* --- Overlap helpers (one physical boat) -------------------- */
function toMin(hhmm) {
  const [h, m] = hhmm.trim().split(":").map(Number);
  return h * 60 + m;
}
function slotsOverlap(timeA, timeB) {
  if (!timeA || !timeB || timeA === timeB) return false;
  const [sA, eA] = timeA.split(" - ").map(toMin);
  const [sB, eB] = timeB.split(" - ").map(toMin);
  return sA < eB && sB < eA;
}

function slotLabel(serviceId, time) {
  if (serviceId === "luxury-yacht")
    return time === "10:00 - 14:00" ? "Morning Charter" : "Sunset Charter";
  if (serviceId === "jet-ski") return time;
  // Speedboat — label each window by time of day
  if (time === "09:00 - 10:00") return "Morning Tour";
  if (time === "09:00 - 10:30") return "Morning Tour";
  if (time === "09:00 - 11:00") return "Morning Tour";
  if (time === "11:00 - 12:00") return "Midday Tour";
  if (time === "12:00 - 13:30") return "Midday Tour";
  if (time === "15:00 - 16:00") return "Afternoon Tour";
  if (time === "15:00 - 16:30") return "Afternoon Tour";
  if (time === "17:00 - 18:30") return "Evening Tour";
  if (time === "18:00 - 19:00") return "Evening Tour";
  if (time === "18:00 - 20:00") return "Evening Tour";
  if (time === "19:00 - 20:30") return "Night Tour";
  return time;
}

function spotsText(serviceId, booked, capacity) {
  const left = capacity - booked;
  if (left <= 0) return "Fully booked";
  if (serviceId === "jet-ski") {
    return `${left}/${capacity} jet ski${left > 1 ? "s" : ""}`;
  }
  if (serviceId === "luxury-yacht") {
    return `${left}/${capacity} spots`;
  }
  return `${left}/${capacity} spots`;
}

/* --- Month grid --------------------------------------------- */
function MonthGrid({
  year,
  month,
  availability,
  blockedDates,
  selectedDate,
  onSelectDate,
  serviceId,
  customSlots,
}) {
  const availByDate = useMemo(() => {
    const map = {};
    availability.forEach((a) => (map[a.date] = a));
    return map;
  }, [availability]);

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstOffset = (new Date(year, month, 1).getDay() + 6) % 7;

  const cells = [];
  for (let i = 0; i < firstOffset; i++)
    cells.push({ empty: true, key: `e${i}` });
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = toDateStr(year, month, d);
    const avail = availByDate[dateStr];
    const blocked = blockedDates?.has(dateStr) || false;
    // Always use default slots as structure; merge only booked counts from live data
    const defSlots = customSlots || getServiceDefaultSlots(serviceId);
    const storedSlots = avail?.timeSlots || [];
    const slots = defSlots.map((ds) => {
      // ONE-BOAT RULE:
      // • Same time string (e.g. both "09:00 - 10:30") = same departure trip.
      //   Shipwreck + Coastal share the boat on that trip → share the 11-seat pool.
      // • Different time string but overlapping = different tour type/duration →
      //   the boat is committed to one trip; any booking there blocks this slot entirely.
      let effectiveBooked;
      if (serviceId === "speedboat" || serviceId === "bayliner") {
        const conflictExists = storedSlots.some(
          (s) =>
            s.time !== ds.time &&
            (s.booked || 0) > 0 &&
            slotsOverlap(s.time, ds.time),
        );
        if (conflictExists) {
          effectiveBooked = ds.capacity; // fully blocked — boat is on a different tour
        } else {
          // Sum only slots with the EXACT same time (same shared trip)
          effectiveBooked = Math.min(
            storedSlots
              .filter((s) => s.time === ds.time)
              .reduce((sum, s) => sum + (s.booked || 0), 0),
            ds.capacity,
          );
        }
      } else {
        effectiveBooked =
          storedSlots.find((s) => s.time === ds.time)?.booked || 0;
      }
      return { ...ds, booked: effectiveBooked };
    });
    // Total remaining seats across this package's time slots
    const slotsLeft = slots.reduce(
      (sum, sl) => sum + Math.max(0, sl.capacity - sl.booked),
      0,
    );
    const isToday = isTodayFn(year, month, d);
    cells.push({
      d,
      dateStr,
      slotsLeft,
      blocked,
      past:
        isBeforeToday(year, month, d) ||
        (isToday && allSlotsExpiredForToday(serviceId, customSlots)),
      today: isToday,
      empty: false,
    });
  }
  const trailing = cells.length % 7 === 0 ? 0 : 7 - (cells.length % 7);
  for (let i = 0; i < trailing; i++) cells.push({ empty: true, key: `t${i}` });

  return (
    <div className="select-none">
      {/* Day headers */}
      <div className="grid grid-cols-7 mb-1">
        {DAY_HEADERS.map((h) => (
          <div
            key={h}
            className="text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wide py-1.5"
          >
            {h}
          </div>
        ))}
      </div>

      {/* Calendar grid — flush grid with thin dividers */}
      <div className="grid grid-cols-7 gap-px bg-gray-100 rounded-xl overflow-hidden border border-gray-100">
        {cells.map((cell, idx) => {
          if (cell.empty) {
            return (
              <div
                key={cell.key || idx}
                className="bg-white min-h-[38px] sm:min-h-[48px]"
              />
            );
          }

          const { d, dateStr, slotsLeft, blocked, past, today } = cell;
          // slotsLeft = total remaining seats across all package slots for this day
          const allFull = slotsLeft === 0;
          const anyBooked =
            availByDate[dateStr]?.timeSlots?.some((s) => (s.booked || 0) > 0) ??
            false;
          const status = allFull ? "full" : anyBooked ? "almost" : "open";
          const isSelected =
            selectedDate &&
            selectedDate.getFullYear() === parseInt(dateStr.split("-")[0]) &&
            selectedDate.getMonth() === parseInt(dateStr.split("-")[1]) - 1 &&
            selectedDate.getDate() === parseInt(dateStr.split("-")[2]);

          if (past) {
            return (
              <div
                key={dateStr}
                className="bg-white min-h-[38px] sm:min-h-[48px] flex items-start justify-center pt-1.5"
              >
                <span className="text-[10px] sm:text-xs text-gray-300 font-medium">
                  {d}
                </span>
              </div>
            );
          }

          // Blocked date — show greyed, completely unclickable
          if (blocked) {
            return (
              <div
                key={dateStr}
                title="This date is not available"
                className="bg-gray-50 min-h-[38px] sm:min-h-[48px] flex flex-col items-center justify-between pt-1.5 pb-1 cursor-not-allowed select-none"
              >
                <span className="text-[10px] sm:text-xs text-gray-300 font-medium">
                  {d}
                </span>
                <span className="text-[8px] sm:text-[9px] text-gray-300 font-medium leading-none mb-0.5">
                  N/A
                </span>
              </div>
            );
          }

          const isFull = status === "full";
          const dotCls = isSelected ? "bg-white/70" : DOT[status];

          return (
            <div
              key={dateStr}
              onClick={() =>
                !isFull && onSelectDate(new Date(dateStr + "T12:00:00"))
              }
              className={[
                "relative min-h-[38px] sm:min-h-[48px] flex flex-col items-center justify-between pt-1.5 pb-1 cursor-pointer transition-colors duration-100",
                isSelected
                  ? "bg-blue-600"
                  : isFull
                    ? "bg-white cursor-not-allowed"
                    : today
                      ? "bg-cyan-50 hover:bg-cyan-100"
                      : "bg-white hover:bg-slate-50",
              ].join(" ")}
            >
              {/* Date */}
              <span
                className={[
                  "text-[11px] sm:text-xs font-semibold leading-none",
                  isSelected
                    ? "text-white"
                    : today
                      ? "text-cyan-600"
                      : isFull
                        ? "text-red-400"
                        : "text-gray-700",
                ].join(" ")}
              >
                {today && !isSelected ? (
                  <span className="inline-flex items-center justify-center w-[17px] h-[17px] sm:w-[19px] sm:h-[19px] rounded-full bg-cyan-500 text-white text-[8px] sm:text-[9px] font-bold">
                    {d}
                  </span>
                ) : (
                  d
                )}
              </span>

              {/* Status dot */}
              <span
                className={`w-[6px] h-[6px] sm:w-[7px] sm:h-[7px] rounded-full ${dotCls}`}
              />

              {/* Slots left counter */}
              <span
                className={[
                  "text-[8px] sm:text-[9px] font-medium leading-none tabular-nums",
                  isSelected ? "text-white/70" : "text-gray-400",
                ].join(" ")}
              >
                {isFull ? (
                  <span className="text-red-400 font-semibold">full</span>
                ) : slotsLeft !== null && slotsLeft !== undefined ? (
                  slotsLeft
                ) : (
                  "\u00a0"
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* --- Main component ----------------------------------------- */
const AvailabilityCalendar = ({
  serviceId,
  onDateSelect,
  onTimeSlotSelect,
  customSlots,
}) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const dateRange = useMemo(() => {
    const y = currentMonth.getFullYear();
    const m = currentMonth.getMonth();
    return { startDate: new Date(y, m, 1), endDate: new Date(y, m + 1, 0) };
  }, [currentMonth]);

  const { availability, blockedDates, loading } = useAvailability(
    serviceId,
    dateRange.startDate,
    dateRange.endDate,
  );

  const handleSelectDate = useCallback(
    (date) => {
      setSelectedDate(date);
      setSelectedTimeSlot(null);
      if (onDateSelect) onDateSelect(date);
    },
    [onDateSelect],
  );

  const handleSelectSlot = useCallback(
    (slotTime, remaining) => {
      setSelectedTimeSlot(slotTime);
      if (onTimeSlotSelect) onTimeSlotSelect(slotTime, remaining);
    },
    [onTimeSlotSelect],
  );

  const prevMonth = () =>
    setCurrentMonth((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const nextMonth = () =>
    setCurrentMonth((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const monthLabel = currentMonth.toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  });

  const selectedDateSlots = useMemo(() => {
    if (!selectedDate) return null;
    const dateStr = toDateStr(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
    );
    // Don't show slots for blocked dates
    if (blockedDates?.has(dateStr)) return null;
    const avail = availability.find((a) => a.date === dateStr);
    const defSlots = customSlots || getServiceDefaultSlots(serviceId);
    // All stored slots for this date (includes every package's slot)
    const allStoredSlots = avail?.timeSlots || [];
    return defSlots.map((ds) => {
      // One-boat rule: the effective number of people on the boat at this time
      // = sum of booked counts from ALL stored slots that overlap this window.
      // This means 11 is always the true max for any given time period.
      // Two non-overlapping slots can independently have 11 each (22 total/day).
      // ONE-BOAT RULE (same as MonthGrid):
      // • Same exact time string = shared trip → sum those bookings (share 11 seats)
      // • Different time string but overlapping = different tour → block slot entirely
      let effectiveBooked;
      if (serviceId === "speedboat" || serviceId === "bayliner") {
        const conflictExists = allStoredSlots.some(
          (s) =>
            s.time !== ds.time &&
            (s.booked || 0) > 0 &&
            slotsOverlap(s.time, ds.time),
        );
        if (conflictExists) {
          effectiveBooked = ds.capacity; // boat is on a different tour — show Fully booked
        } else {
          effectiveBooked = Math.min(
            allStoredSlots
              .filter((s) => s.time === ds.time)
              .reduce((sum, s) => sum + (s.booked || 0), 0),
            ds.capacity,
          );
        }
      } else {
        effectiveBooked =
          allStoredSlots.find((s) => s.time === ds.time)?.booked || 0;
      }
      return { ...ds, booked: effectiveBooked };
    });
  }, [selectedDate, availability, blockedDates, serviceId, customSlots]);

  const selectedDateStr = selectedDate
    ? selectedDate.toLocaleDateString("en-GB", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : null;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
        <h2 className="text-sm sm:text-base font-bold text-white">
          Select a Date
        </h2>
        <div className="flex items-center gap-3 sm:gap-4">
          {[
            ["bg-emerald-500", "Open"],
            ["bg-orange-500", "Filling"],
            ["bg-red-500", "Full"],
          ].map(([c, l]) => (
            <span
              key={l}
              className="flex items-center gap-1 text-[9px] sm:text-[10px] text-slate-300"
            >
              <span className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${c}`} />
              {l}
            </span>
          ))}
        </div>
      </div>

      <div className="px-3 sm:px-4 pb-4 pt-3">
        {/* Month navigator */}
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={prevMonth}
            aria-label="Previous month"
            className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-500 transition"
          >
            <svg
              className="w-3 h-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <span className="text-sm font-bold text-gray-800">{monthLabel}</span>
          <button
            onClick={nextMonth}
            aria-label="Next month"
            className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-500 transition"
          >
            <svg
              className="w-3 h-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>

        {/* Calendar */}
        {loading ? (
          <div className="flex items-center justify-center h-44 gap-2 text-gray-400">
            <svg
              className="animate-spin w-4 h-4 text-blue-400"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
            <span className="text-xs">Loading...</span>
          </div>
        ) : (
          <MonthGrid
            year={year}
            month={month}
            availability={availability}
            blockedDates={blockedDates}
            selectedDate={selectedDate}
            onSelectDate={handleSelectDate}
            serviceId={serviceId}
            customSlots={customSlots}
          />
        )}

        {/* Time slot panel */}
        {selectedDate && selectedDateSlots && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-xs font-bold text-gray-900">
                  {serviceId === "luxury-yacht"
                    ? "Charter Availability"
                    : "Choose a Time Slot"}
                </p>
                <p className="text-[10px] text-gray-400 mt-0.5">
                  {selectedDateStr}
                </p>
              </div>
              <button
                onClick={() => {
                  setSelectedDate(null);
                  setSelectedTimeSlot(null);
                  if (onDateSelect) onDateSelect(null);
                }}
                className="text-[11px] text-gray-400 hover:text-gray-700 px-2 py-1 rounded-md hover:bg-gray-100 transition"
              >
                Change
              </button>
            </div>

            <div
              className={`grid gap-2 ${selectedDateSlots.length > 2 ? "grid-cols-2" : "grid-cols-1 sm:grid-cols-2"}`}
            >
              {selectedDateSlots.map((slot) => {
                const left = slot.capacity - (slot.booked || 0);
                const isFull = left <= 0;
                const isExpired = isSlotExpiredForDate(selectedDate, slot.time);
                const isDisabled = isFull || isExpired;
                const isActive = selectedTimeSlot === slot.time;
                const slotFill =
                  slot.capacity > 0 ? (slot.booked || 0) / slot.capacity : 0;
                const dotCls = DOT[getStatus(slot.booked || 0, slot.capacity)];

                return (
                  <button
                    key={slot.time}
                    onClick={() =>
                      !isDisabled && handleSelectSlot(slot.time, left)
                    }
                    disabled={isDisabled}
                    className={[
                      "text-left p-3 rounded-xl border-2 transition-all duration-150 w-full",
                      isActive
                        ? "border-blue-500 bg-blue-50"
                        : isDisabled
                          ? "border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed"
                          : "border-gray-200 hover:border-blue-300 hover:bg-blue-50/40",
                    ].join(" ")}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p
                          className={`text-[11px] font-bold ${isActive ? "text-blue-700" : "text-gray-800"}`}
                        >
                          {slotLabel(serviceId, slot.time)}
                        </p>
                        <p className="text-[10px] text-gray-400">{slot.time}</p>
                      </div>
                      <div className="flex items-center gap-1 pt-0.5">
                        {isActive && (
                          <svg
                            className="w-3 h-3 text-blue-500 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={3}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        )}
                        <span
                          className={`w-2 h-2 rounded-full flex-shrink-0 ${dotCls}`}
                        />
                      </div>
                    </div>

                    <p
                      className={`text-[10px] font-semibold mt-1.5 ${isDisabled ? "text-red-500" : isActive ? "text-blue-600" : "text-gray-500"}`}
                    >
                      {isFull
                        ? "Fully booked"
                        : isExpired
                          ? "Too late to book"
                          : spotsText(
                              serviceId,
                              slot.booked || 0,
                              slot.capacity,
                            )}
                    </p>

                    {!isDisabled && slot.capacity > 0 && (
                      <div className="mt-2 h-[3px] bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${dotCls}`}
                          style={{ width: `${Math.min(100, slotFill * 100)}%` }}
                        />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Pricing hint */}
            {serviceId === "luxury-yacht" && (
              <div className="mt-3 px-3 py-2 bg-amber-50 border border-amber-100 rounded-lg">
                <p className="text-[11px] text-amber-800">
                  <strong>&#8364;600</strong> base (up to 5 guests)
                  &nbsp;&bull;&nbsp; +<strong>&#8364;50</strong> per extra guest
                  &nbsp;&bull;&nbsp; max 10 guests
                </p>
              </div>
            )}
            {serviceId === "jet-ski" && (
              <div className="mt-3 px-3 py-2 bg-sky-50 border border-sky-100 rounded-lg">
                <p className="text-[11px] text-sky-800">
                  15 min: <strong>&#8364;65</strong> &nbsp;&bull;&nbsp; 20 min:{" "}
                  <strong>&#8364;80</strong> &nbsp;&bull;&nbsp; 30 min:{" "}
                  <strong>&#8364;120</strong>
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AvailabilityCalendar;
